import dayjs from 'dayjs'

export const sleep = (sec: number) => new Promise((res) => setTimeout(res, sec))

export const fetchDelay = async function <TModel>(action: () => Promise<TModel>, sec: number) {
  const [res] = await Promise.all([action(), sleep(sec)])
  return res
}

export const isGuid = (value: string): boolean => {
  const guidRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/
  return guidRegex.test(value)
}

export const isValidEmail = (value: string) => {
  const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
  return regex.test(value)
}

export const isValidPhoneNumber = (phoneNumber?: string): boolean => {
  const phoneRegex = /^(\+?\d{1,4}[\s-]?)?((\(\d{1,4}\))|\d{1,4})[\s-]?\d{1,4}[\s-]?\d{1,9}$/
  return phoneRegex.test((phoneNumber ?? '').trim())
}

export const isValidURL = (url: string): boolean => {
  const regex = /^(https?|ftp):\/\/[^\s/$.?#].[^\s]*$/i
  return regex.test(url)
}

export const windowScrollToTop = (options?: ScrollToOptions, delay?: number) => {
  setTimeout(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth', ...options })
  }, delay || 50)
}

export const mergeClasses = (...classes: string[]) => {
  return classes.join(' ')
}

//#region Object
const isObject = (obj: any) => obj != null && typeof obj === 'object' && !Array.isArray(obj)

type DeepPartial<T> = { [P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P] } | undefined

/**
 * mergeObjects: merge objects deeply, later sources always override earlier values for the same key.
 *
 * Rules:
 * - undefined  → skip, keep previous value
 * - null       → explicitly remove the field
 * - plain obj  → recurse (deep merge)
 * - everything else (primitive, array, false, 0) → source wins
 *
 * @example
 * // undefined is skipped — previous value kept
 * mergeObjects({ a: 1 }, { a: undefined })
 * // → { a: 1 }
 *
 * // null removes the field
 * mergeObjects({ a: { x: 1 } }, { a: null })
 * // → { a: null }
 *
 * // Primitive override — source wins
 * mergeObjects({ a: 1, b: true }, { b: false })
 * // → { a: 1, b: false }
 *
 * // Nested deep merge — both sides are plain objects → recurse
 * mergeObjects({ a: { x: 1, y: 2 } }, { a: { x: 10, z: 3 } })
 * // → { a: { x: 10, y: 2, z: 3 } }
 *
 * // Multiple sources — last non-undefined source wins on conflict
 * mergeObjects({ a: 1 }, { a: 2 }, { a: 3 })
 * // → { a: 3 }
 */
export const mergeObjects = <T>(...objects: DeepPartial<T>[]): T => {
  const validObjects = objects.filter((x): x is Exclude<DeepPartial<T>, undefined> => x != null)
  if (validObjects.length === 0) return {} as T

  const [first, ...rest] = validObjects
  const target = Object.assign({}, first) as any

  for (const obj of rest) {
    for (const key of Object.keys(obj)) {
      const srcVal = (obj as any)[key]

      // undefined → skip, keep current value
      if (srcVal === undefined) continue

      const tgtVal = target[key]
      if (isObject(tgtVal) && isObject(srcVal)) {
        // both plain objects → recurse
        target[key] = mergeObjects(tgtVal, srcVal)
      } else {
        // null, false, 0, array, primitive → source wins
        target[key] = srcVal
      }
    }
  }

  return target
}

export const cleanObject = <T extends object>(obj: T): Partial<T> => {
  try {
    if (!obj) return {}
    return Object.entries(obj).reduce((acc, [key, value]) => {
      if (value !== undefined) {
        ;(acc as any)[key] = value
      }
      return acc
    }, {} as Partial<T>)
  } catch (error) {
    return {}
  }
}

export const deepRemoveEmptyFields = <T extends object>(obj: T): T => {
  if (typeof obj !== 'object' || obj === null) return obj
  const result: any = Array.isArray(obj) ? [] : {}
  Object.entries(obj).forEach(([key, value]) => {
    if (
      value === undefined ||
      value === null ||
      (typeof value === 'string' && value.trim() === '') ||
      (typeof value === 'number' && isNaN(value)) ||
      (Array.isArray(value) && value.length === 0) ||
      (typeof value === 'object' && !Array.isArray(value) && Object.keys(deepRemoveEmptyFields(value)).length === 0)
    ) {
      // skip
    } else if (typeof value === 'object' && !Array.isArray(value)) {
      const cleaned = deepRemoveEmptyFields(value)
      if (Object.keys(cleaned).length > 0) result[key] = cleaned
    } else {
      result[key] = value
    }
  })
  return result
}
//#endregion

export const encodeBase64 = (input: string): string => {
  try {
    const utf8Bytes = new TextEncoder().encode(input)
    let binaryString = ''
    utf8Bytes.forEach((byte) => {
      binaryString += String.fromCharCode(byte)
    })
    return btoa(binaryString)
  } catch (error) {
    console.error('Error encoding to base64', error)
    return ''
  }
}

export const decodeBase64 = (encoded: string): string | undefined => {
  try {
    const binaryString = atob(encoded)
    const utf8Bytes = new Uint8Array(binaryString.length)
    for (let i = 0; i < binaryString.length; i++) {
      utf8Bytes[i] = binaryString.charCodeAt(i)
    }
    return new TextDecoder().decode(utf8Bytes)
  } catch (error) {
    console.error('Error decoding base64', error)
    return
  }
}

//#region Try Parse
export const tryParseObject = function <T>(value: any, defaultValue: T): T {
  try {
    if (!value) return defaultValue
    return JSON.parse(value)
  } catch {
    return defaultValue
  }
}

export const tryParseArray = function <T>(value: any, defaultValue: T[] = []): T[] {
  try {
    if (!value) return []
    const parseValue = JSON.parse(value)
    return Array.isArray(parseValue) ? parseValue : []
  } catch {
    return defaultValue
  }
}

export const tryParseIntRequired = function (value: any, defaultValue: number): number {
  try {
    if (!value) return defaultValue
    return parseInt(value)
  } catch {
    return defaultValue
  }
}

export const tryParseCsvFileToArray = (file: File): Promise<Record<string, string>[]> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = () => {
      try {
        const text = reader.result as string
        const rows = text.split(/\r?\n/).filter(Boolean)

        if (rows.length < 1) return resolve([])

        const headers = rows[0].split(',').map((h) => h.trim())

        const data = rows.slice(1).map((row) => {
          const values = row.split(',').map((v) => v.trim())
          const item: Record<string, string> = {}

          headers.forEach((header, index) => {
            item[header] = values[index] ?? ''
          })

          return item
        })

        resolve(data)
      } catch (error) {
        resolve([])
      }
    }

    reader.onerror = () => {
      reject(new Error('Error reading file'))
    }

    reader.readAsText(file)
  })
}
//#endregion

//#region Format
export const formatFileSize = (sizeInKb: number) => {
  if (sizeInKb < 1024) {
    return sizeInKb.toFixed(2) + ' Kb'
  } else if (sizeInKb < 1024 * 1024) {
    return (sizeInKb / 1024).toFixed(2) + ' Mb'
  } else if (sizeInKb < 1024 * 1024 * 1024) {
    return (sizeInKb / (1024 * 1024)).toFixed(2) + ' Gb'
  } else {
    return (sizeInKb / (1024 * 1024 * 1024)).toFixed(2) + ' Tb'
  }
}

export const formatDuration = (duration?: number): string => {
  const isNotValidDuration = duration === undefined || duration === null
  const isNotNumber = typeof duration === 'string' && isNaN(Number(duration))
  if (isNotValidDuration || isNotNumber) return '00:00:00'

  // Round the duration to the nearest second
  const totalSeconds = Math.round(duration)

  // Calculate hours, minutes, and seconds
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60

  // Format each component to ensure it has two digits
  const hoursString = String(hours).padStart(2, '0')
  const minutesString = String(minutes).padStart(2, '0')
  const secondsString = String(seconds).padStart(2, '0')

  // Combine the components into a single string
  return `${hoursString}:${minutesString}:${secondsString}`
}

export const formatCurrency = (value?: any, prefix = '$ ', suffix = ''): string => {
  let parsedValue
  try {
    parsedValue = parseFloat(value)
    if (isNaN(parsedValue)) parsedValue = 0
  } catch (e) {
    parsedValue = 0
  }
  const roundedValue = parsedValue.toFixed(2)
  const [integerPart, decimalPart] = roundedValue.split('.')
  const formattedIntegerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')

  let formattedValue = formattedIntegerPart
  if (decimalPart !== '00') {
    formattedValue = `${formattedIntegerPart}.${decimalPart}`
  }
  return `${prefix}${formattedValue}${suffix}`
}

export const formatNumberWithCommas = (number: number): string => {
  return number.toLocaleString('en-US')
}

export const formatCapitalizeFirstText = (value: string = '') => {
  if (!value) return value
  const [first, ...data] = Array.from(value)
  return `${first.toUpperCase()}${data.join('')}`
}

export type FormatDatetimeStyle = 'style1' | 'style2' | 'style3'

export const formatDatetimeStyles: Record<FormatDatetimeStyle, string> = {
  style1: 'DD/MM/YYYY HH:mm',
  style2: 'MMMM D, YYYY',
  style3: 'MM-DD-YYYY'
}

/**
 * Formats a datetime string using one of the predefined styles or a custom format string.
 *
 * Available format styles:
 * - style1: DD/MM/YYYY HH:mm → e.g., '25/04/2025 14:30'
 * - style2: MMMM D, YYYY → e.g., 'April 25, 2025'
 * - style3: MM-DD-YYYY → e.g., '04-25-2025'
 * - Or any custom dayjs format string → e.g., 'YYYY-MM-DD', 'HH:mm:ss'
 *
 * @param value - A datetime string (ISO format or any format parsable by dayjs).
 * @param format - Format style key ('style1', 'style2', 'style3') or custom dayjs format string. Defaults to 'style1'.
 * @returns A formatted datetime string, or 'unknown' if the input is invalid or unparsable.
 */
export const formatDatetime = (value: string, format: FormatDatetimeStyle | (string & {}) = 'style1'): string => {
  try {
    if (!value) throw new Error()
    const finalFormat = formatDatetimeStyles[format as FormatDatetimeStyle] ?? format
    return dayjs(value).format(finalFormat)
  } catch (error) {
    return 'unknown'
  }
}
//#endregion
